import { useId, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { FIELD_CLASS } from '@/components/properties/AddressFields';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/lib/format';
import { centsToNumber } from '@/lib/money';
import {
  lineCents,
  TAX_CATEGORIES,
  validateLine,
  type DraftLine,
  type TaxCategory,
} from '@/lib/estimateLines';

/**
 * One editable line of a draft estimate (M2-06).
 *
 * Every field is labelled for a screen reader (the visible column headings
 * are for the eye), errors sit OUTSIDE the labels and are tied back with
 * `aria-describedby` — a label that contains its own error changes the
 * field's accessible name, which M2-52 found the hard way — and the line is
 * a `role="group"` named "Line 3", so "Quantity, Line 3" is what is heard.
 *
 * Alt+↑ / Alt+↓ from anywhere in the line moves it, and focus stays in the
 * field it was in: reordering is part of "keyboard-driven", and the move
 * buttons are its pointer-and-touch equivalent.
 */
export function EstimateLineRow({
  line,
  index,
  count,
  showErrors,
  onChange,
  onMove,
  onRemove,
}: {
  line: DraftLine;
  index: number;
  count: number;
  /** False until the line has been left once, so a half-typed line is not shouted at. */
  showErrors: boolean;
  onChange: (line: DraftLine) => void;
  onMove: (by: -1 | 1) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const number = index + 1;
  const errors = showErrors ? (validateLine(line) ?? {}) : {};
  const cents = lineCents(line);
  // Hours and a rate: a typed labour row (M2-06, read live from its tax
  // category, so switching a custom row to Labor relabels it at once) and, since
  // M2-33, a line from an hourly labor item. ⚠️ Not `hourly` alone (found in
  // review): a custom row switched to Labor kept "Quantity" until it was saved
  // and reopened.
  const isLabour = line.hourly || (line.taxCategory === 'Labor' && line.catalogItemId === null);

  const set = (patch: Partial<DraftLine>) => onChange({ ...line, ...patch });
  const errorId = (field: string) => `${id}-${field}-error`;
  const describedBy = (field: keyof typeof errors) => (errors[field] ? errorId(field) : undefined);

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (!event.altKey || (event.key !== 'ArrowUp' && event.key !== 'ArrowDown')) return;
    event.preventDefault();
    onMove(event.key === 'ArrowUp' ? -1 : 1);
  }

  return (
    // The keys belong to the fields inside; this only listens for Alt+↑/↓ as
    // they bubble. On a plain wrapper because jsx-a11y (rightly) will not have
    // a listener on the named group itself.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div onKeyDown={onKeyDown}>
      <div
        role="group"
        aria-label={t('estimates.line.rowLabel', { number })}
        data-testid="estimate-line"
        className={`grid grid-cols-2 gap-2 rounded-md border border-[var(--color-border)] p-3 ${LINE_GRID} md:items-start md:border-0 md:p-0`}
      >
        <div className="col-span-2 md:col-span-1">
          <Caption text={t('estimates.line.description')} />
          <input
            className={FIELD_CLASS}
            aria-label={t('estimates.line.description')}
            value={line.description}
            maxLength={600}
            aria-invalid={errors.description ? true : undefined}
            aria-describedby={describedBy('description')}
            onChange={(event) => set({ description: event.target.value })}
          />
          {errors.description ? (
            <FieldError id={errorId('description')} message={t(errors.description)} />
          ) : null}
        </div>

        <div>
          <Caption
            text={isLabour ? t('estimates.line.quantityHours') : t('estimates.line.quantity')}
          />
          <input
            className={cn(FIELD_CLASS, 'text-right tabular-nums')}
            inputMode="decimal"
            aria-label={isLabour ? t('estimates.line.quantityHours') : t('estimates.line.quantity')}
            value={line.quantity}
            aria-invalid={errors.quantity ? true : undefined}
            aria-describedby={describedBy('quantity')}
            onChange={(event) => set({ quantity: event.target.value })}
          />
          {errors.quantity ? (
            <FieldError id={errorId('quantity')} message={t(errors.quantity)} />
          ) : null}
        </div>

        <div>
          <Caption text={isLabour ? t('estimates.line.rate') : t('estimates.line.unitPrice')} />
          <input
            className={cn(FIELD_CLASS, 'text-right tabular-nums')}
            inputMode="decimal"
            aria-label={isLabour ? t('estimates.line.rate') : t('estimates.line.unitPrice')}
            value={line.unitPrice}
            aria-invalid={errors.unitPrice ? true : undefined}
            aria-describedby={describedBy('unitPrice')}
            onChange={(event) => set({ unitPrice: event.target.value })}
          />
          {errors.unitPrice ? (
            <FieldError id={errorId('unitPrice')} message={t(errors.unitPrice)} />
          ) : null}
        </div>

        <div>
          <Caption text={t('estimates.line.taxCategory')} />
          <select
            className={FIELD_CLASS}
            aria-label={t('estimates.line.taxCategory')}
            value={line.taxCategory}
            onChange={(event) => set({ taxCategory: event.target.value as TaxCategory })}
          >
            {TAX_CATEGORIES.map((category) => (
              <option key={category} value={category}>
                {t(`estimates.line.taxCategories.${category}`)}
              </option>
            ))}
          </select>
        </div>

        <div className="col-span-2 md:col-span-1">
          <Caption text={t('estimates.line.discount')} />
          <div className="flex gap-2">
            <select
              className={cn(FIELD_CLASS, 'w-28 shrink-0')}
              aria-label={t('estimates.line.discount')}
              value={line.discountType}
              onChange={(event) =>
                set({
                  discountType: event.target.value as DraftLine['discountType'],
                  discountValue: event.target.value === '' ? '' : line.discountValue,
                })
              }
            >
              <option value="">{t('estimates.line.discountNone')}</option>
              <option value="Percent">{t('estimates.line.discountPercent')}</option>
              <option value="Amount">{t('estimates.line.discountAmount')}</option>
            </select>
            {line.discountType !== '' ? (
              <input
                className={cn(FIELD_CLASS, 'min-w-0 text-right tabular-nums')}
                inputMode="decimal"
                aria-label={t('estimates.line.discountValue')}
                value={line.discountValue}
                aria-invalid={errors.discountValue ? true : undefined}
                aria-describedby={describedBy('discountValue')}
                onChange={(event) => set({ discountValue: event.target.value })}
              />
            ) : null}
          </div>
          {errors.discountValue ? (
            <FieldError id={errorId('discountValue')} message={t(errors.discountValue)} />
          ) : null}
        </div>

        <div
          className="flex h-9 flex-col items-end justify-center text-sm tabular-nums"
          data-testid="line-total"
        >
          <span className="font-medium">
            <span className="sr-only">{t('estimates.line.total')} </span>
            {cents ? formatCurrency(centsToNumber(cents.total)) : '—'}
          </span>
          {cents && cents.discount > 0n ? (
            <span className="text-xs text-[var(--color-muted-foreground)]">
              {t('estimates.line.discountTaken', {
                amount: formatCurrency(centsToNumber(cents.discount)),
              })}
            </span>
          ) : null}
        </div>

        <div className="col-span-2 flex justify-end gap-1 md:col-span-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label={t('estimates.line.moveUp', { number })}
            disabled={index === 0}
            onClick={() => onMove(-1)}
          >
            <span aria-hidden="true">↑</span>
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label={t('estimates.line.moveDown', { number })}
            disabled={index === count - 1}
            onClick={() => onMove(1)}
          >
            <span aria-hidden="true">↓</span>
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label={t('estimates.line.remove', { number })}
            onClick={onRemove}
          >
            <span aria-hidden="true">✕</span>
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * A field's name, visible on a phone where the lines stack and there are no
 * column headings (the builder's header row does the job from `md`). Hidden
 * from assistive technology: every field carries its own accessible name, and
 * reading it twice would be noise.
 */
function Caption({ text }: { text: string }) {
  return (
    <span
      aria-hidden="true"
      className="mb-1 block text-xs text-[var(--color-muted-foreground)] md:hidden"
    >
      {text}
    </span>
  );
}

/** The builder's column headings from `md` up — the same grid as a line. */
export const LINE_GRID = 'md:grid-cols-[minmax(0,3fr)_5rem_7rem_7rem_minmax(0,2fr)_6rem_7rem]';

function FieldError({ id, message }: { id: string; message: string }) {
  return (
    <p id={id} className="mt-1 text-xs text-[var(--color-destructive)]">
      {message}
    </p>
  );
}
